"use client"
import { allCourses } from '@/pages/courses/data/courses'
import { ArrowLeft, CheckCircle2, Star, HelpCircle, ArrowRight, Target, Award, TrendingUp, Quote, Sparkles, CheckCircle, X } from 'lucide-react'
import { motion } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import { Header } from '@/pages/home/sections/header'
import { Footer } from '@/pages/home/sections/footer'
import { useEffect, useState } from 'react'
import styles from './course-detail.module.css'
import { CourseBadges } from '@/components/course-badge'
import { CourseCard } from '@/components/course-card'

export function CourseDetailView() {
  const { id } = useParams()
  const navigate = useNavigate()
  const course = allCourses.find(c=>c.id===id)
  const [isVideoOpen, setIsVideoOpen] = useState(false)

  useEffect(()=> { window.scrollTo({ top:0, behavior:'instant' as ScrollBehavior }) }, [id])

  if(!course) {
    return (
      <div className="min-h-screen bg-[#FCF8F1]">
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-24 text-center">
          <h1 className="text-3xl font-bold mb-4">Course Not Found</h1>
          <p className="text-gray-600 mb-8">The course you are looking for does not exist or was removed.</p>
          <button onClick={()=>navigate('/courses')} className="px-6 py-3 rounded-lg bg-gradient-to-r from-[#245E51] to-[#1a4a3e] text-white font-semibold shadow hover:shadow-md transition">Back to Courses</button>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#FCF8F1]">
      <Header />
      <main>
        {/* Hero */}
        <section className={styles.heroSection}>
          <div className={styles.sectionContainer}>
            <div className={styles.heroGrid}>
              <div className={styles.heroContent}>
                <button onClick={()=>navigate(-1)} className={styles.heroBackButton}>
                  <ArrowLeft className={styles.heroBackIcon} /> Back
                </button>
                <h1 className={styles.heroTitle}>{course.title}</h1>
                <p className={styles.heroDescription}>{course.description}</p>
                <div className={styles.heroBadges}>
                  <span className={`${styles.heroBadge} ${styles.heroBadgePrimary}`}>{course.category}</span>
                  {course.badges && course.badges.length > 0 && (
                    <div className="flex gap-2">
                      <CourseBadges badges={course.badges} />
                    </div>
                  )}
                </div>
                <div className={styles.heroPricing}>
                  <a href="/free-trial" className={styles.heroCtaButton}>Request Free Trial</a>
                </div>
              </div>
              <div className={styles.heroImageWrap}>
                <div className={styles.heroImage}>
                  {course.videoUrl ? (
                    <div className="relative w-full h-full">
                      <img src={course.image || '/placeholder.jpg'} alt={course.imageAlt || course.title} className="w-full h-full object-cover rounded-2xl" />
                      <button
                        type="button"
                        onClick={() => setIsVideoOpen(true)}
                        className="group absolute bottom-4 right-4 flex items-center gap-2 rounded-full bg-white/90 backdrop-blur px-3 py-2 shadow-xl ring-1 ring-black/10 hover:bg-white transition"
                        aria-label="Play course preview video"
                      >
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#245E51]/10">
                          <img
                            src="/video-playback-3.svg"
                            alt="Play course preview"
                            className="h-5 w-5"
                          />
                        </span>
                        <span className="text-[11px] font-semibold text-gray-900 hidden sm:inline">Preview</span>
                      </button>
                    </div>
                  ) : (
                    <img src={course.image || '/placeholder.jpg'} alt={course.imageAlt || course.title} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Video Modal */}
        {isVideoOpen && course.videoUrl && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
            role="dialog"
            aria-modal="true"
            onClick={() => setIsVideoOpen(false)}
          >
            <div 
              className="relative w-full max-w-5xl rounded-2xl bg-black overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setIsVideoOpen(false)}
                className="absolute right-4 top-4 z-10 rounded-full bg-white/90 p-2 text-gray-900 hover:bg-white transition-colors"
                aria-label="Close video preview"
              >
                <X className="h-5 w-5" />
              </button>
              <video
                src={course.videoUrl}
                controls
                autoPlay
                className="w-full aspect-video"
              >
                <source src={course.videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        )}

        {/* Outcomes - Modern Design */}
        {course.outcomes && course.outcomes.length > 0 && (
          <section className="py-24 bg-gradient-to-br from-[#FCF8F1] via-white to-[#FCF8F1] relative overflow-hidden">
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-0 left-0 w-96 h-96 bg-[#A8FF51]/20 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#245E51]/10 rounded-full blur-3xl"></div>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center max-w-3xl mx-auto mb-16"
              >
                <span className="inline-flex items-center px-4 py-2 text-sm font-semibold text-[#245E51] bg-[#A8FF51]/20 rounded-full border border-[#A8FF51]/30 mb-6">
                  <Target className="w-4 h-4 mr-2" />
                  Course Outcomes
                </span>
                <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight mb-6">
                  What You'll <span className="text-transparent bg-gradient-to-r from-[#245E51] to-[#A8FF51] bg-clip-text">Achieve</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Build lasting skills through guided practice, expert feedback, and a supportive learning environment.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {course.outcomes.map((outcome, idx) => {
                  const icons = [Target, Award, TrendingUp, CheckCircle2, Star, Sparkles]
                  const Icon = icons[idx % icons.length]
                  return (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ duration: 0.6, delay: idx * 0.1 }}
                      className="group relative"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-[#A8FF51]/10 to-transparent rounded-3xl transform group-hover:scale-105 transition-transform duration-300"></div>
                      <div className="relative bg-white/80 backdrop-blur-xl rounded-3xl border border-white/60 shadow-xl p-8 hover:shadow-2xl transition-all duration-300">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#245E51] to-[#1a4a3e] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        <p className="text-gray-700 leading-relaxed font-medium">{outcome}</p>
                        <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#A8FF51]/20 flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-[#245E51]" />
                        </div>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </section>
        )}


        {/* Modern CTA Section */}
        <section className={styles.ctaSection}>
          <div className={styles.sectionContainer}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className={styles.ctaInner}
            >
              <span className={styles.ctaBadge}>
                <Sparkles className={styles.ctaBadgeIcon} />
                Start Your Trial
              </span>
              <h2 className={styles.ctaTitle}>
                Begin Your <span className={styles.ctaTitleHighlight}>Learning Journey</span> Today
              </h2>
              <p className={styles.ctaBody}>
                Experience live coaching, personalized feedback, and a clear roadmap. Start your free trial lesson and discover the Dhikr Institute difference.
              </p>

              <div className={styles.ctaActions}>
                <a href="/free-trial" className={styles.ctaPrimary}>Request Free Trial</a>
                <a href="/contact" className={styles.ctaSecondary}>Contact Us</a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Reviews Section - Modern Testimonials */}
        <section className={styles.reviewsSection}>
          <div className={styles.sectionContainer}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={styles.reviewsHeader}
            >
              <span className={styles.reviewsBadge}>
                <Star className={styles.reviewsBadgeIcon} />
                Student Success
              </span>
              <h2 className={styles.reviewsTitle}>
                <span className={styles.reviewsHighlight}>Transformative</span> Experiences
              </h2>
              <div className={styles.reviewsMeta}>
                <div className={styles.reviewsMetaStars}>
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={styles.reviewsMetaStar} />
                  ))}
                </div>
              </div>
            </motion.div>

            <div className={styles.reviewsGrid}>
              {[
                { name: 'Aisha M.', review: 'This course transformed my recitation confidence. The structured approach and personalized feedback made all the difference.', rating: 5, course: 'Tajweed Master' },
                { name: 'Yusuf K.', review: 'Clear methodology, engaging instructors, and practical daily application. Best investment in my Islamic education.', rating: 5, course: 'Quran Memorization' },
                { name: 'Maryam A.', review: 'The pacing was perfect and the feedback sessions helped me overcome my pronunciation challenges quickly.', rating: 5, course: 'Arabic Fluency' }
              ].map((review, idx) => (
                <motion.div
                  key={review.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.15, duration: 0.6 }}
                  className={styles.reviewCardWrapper}
                >
                  <div className={styles.reviewGlow} />
                  <div className={styles.reviewCard}>
                    <Quote className={styles.reviewQuoteIcon} />
                    <p className={styles.reviewCopy}>{review.review}</p>
                    <div className={styles.reviewFooter}>
                      <div>
                        <div className={styles.reviewAuthor}>{review.name}</div>
                        <div className={styles.reviewCourse}>{review.course}</div>
                      </div>
                      <div className={styles.reviewRating}>
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} className={styles.reviewRatingIcon} />
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            
          </div>
        </section>

        {/* FAQ (glass panel) */}
        <section className="py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold tracking-tight text-gray-900 mb-10">Frequently Asked Questions</h2>
            <div className="space-y-5">
              {[{
                q:'How are live sessions conducted?',
                a:'Sessions take place via secure video classroom with interactive whiteboard and recitation feedback.'
              },{
                q:'Can I pause or extend enrollment?',
                a:'Yes, you can request schedule adjustments; memorization programs include structured revision breaks.'
              },{
                q:'Do I get completion certification?',
                a:'Yes, a digital certificate is issued upon successfully meeting assessment criteria.'
              }].map(item => (
                <div key={item.q} className="group rounded-2xl border border-white/60 bg-white/85 backdrop-blur-xl p-6 shadow-xl">
                  <div className="flex items-start gap-4">
                    <div className="mt-1"><HelpCircle className="w-5 h-5 text-[#245E51]" /></div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2 group-hover:text-[#245E51] transition-colors">{item.q}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Courses (enhanced) */}
        <section className="py-20 border-t border-gray-200 bg-white/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-3xl font-bold tracking-tight text-gray-900">Related Courses</h2>
                <p className="text-gray-600 mt-2">Explore more courses in {course.category}</p>
              </div>
              <button onClick={()=>navigate('/courses')} className="inline-flex items-center text-sm font-semibold text-[#245E51] hover:underline">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </button>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {allCourses
                .filter(c => c.category === course.category && c.id !== course.id)
                // Remove duplicates
                .filter((c, index, self) => 
                  index === self.findIndex(course => course.title === c.title)
                )
                .slice(0, 4)
                .map((rc, index) => (
                  <CourseCard key={rc.id} course={rc} index={index} />
                ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}
